import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { FallbackImage } from "../../../Components/FallbackImage.js";
import BrandclubStoreConfigUtils from "../../../utils/brandclubStoreConfigUtils.js";
import { formatMoney } from "../../../utils/stringUtils.js";
import { useDomainConfig } from "../../../api/cache/appConfigQueryOptions.js";
import { getUserRetailerCheckoutOrdersV2 } from "../../api/orderHistoryQueries.js";
import { isOutputState, isCallState } from "./types.js";
import { sectionFadeInStyle } from "./utils.js";
import { StoreBrandingType } from "../../../BrandclubLanding/type.js";
const PAGE_SIZE = 5;
const STATUSES = ["checkout_success", "checkout_in_progress", "checkout_error"];
const GQL_RESPONSE_KEY = "UserRetailerCheckoutOrders";
function transformOrders(rawOrders) {
  return rawOrders.map((order) => ({
    orderId: order.orderId,
    retailerOrderId: order.retailerOrderId,
    orderDate: order.updatedTime,
    status: order.status,
    items: (order.items || []).map((item) => {
      var _a, _b;
      const meta = (_a = order.products) == null ? void 0 : _a[item.stacklineSku];
      return {
        title: (meta == null ? void 0 : meta.title) || item.title || item.retailerSku || "Unknown item",
        imageUrl: (_b = meta == null ? void 0 : meta.imageUrls) == null ? void 0 : _b[0],
        price: item.retailPrice || 0,
        quantity: item.quantity || 1
      };
    }),
    totalAmount: (order.items || []).reduce(
      (sum, item) => sum + (item.retailPrice || 0) * (item.quantity || 1),
      0
    )
  }));
}
function formatDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}
function formatStatus(status) {
  return status.replace(/_/g, " ").replace(/checkout\s*/i, "").trim() || "Processing";
}
function getStatusClass(status) {
  if (status === "checkout_success") return "oh-status-success";
  if (status === "checkout_error") return "oh-status-failed";
  return "oh-status-other";
}
function getOrderDetailPath(storeBrandingType) {
  return storeBrandingType === StoreBrandingType.CustomDTCStore ? "/purchase" : "/dashboard/purchase";
}
function useOrderHistoryPages() {
  const apolloClient = useApolloClient();
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCache, setPageCache] = useState(/* @__PURE__ */ new Map());
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const pageCacheRef = useRef(pageCache);
  pageCacheRef.current = pageCache;
  const fetchPage = useCallback(
    async (page) => {
      var _a;
      if (pageCacheRef.current.has(page)) {
        setCurrentPage(page);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const res = await apolloClient.query({
          context: {
            uri: `${BrandclubStoreConfigUtils.getServerSideSyncGraphQlUri()}?id=getUserRetailerCheckoutOrdersV2`
          },
          query: getUserRetailerCheckoutOrdersV2,
          variables: {
            statuses: STATUSES,
            page: { start: page * PAGE_SIZE, size: PAGE_SIZE }
          },
          fetchPolicy: "network-only"
        });
        const result = (_a = res.data) == null ? void 0 : _a[GQL_RESPONSE_KEY];
        const orders = transformOrders((result == null ? void 0 : result.items) ?? []);
        setPageCache((prev) => new Map(prev).set(page, orders));
        setTotal((result == null ? void 0 : result.total) ?? 0);
        setCurrentPage(page);
      } catch (err) {
        setError((err == null ? void 0 : err.message) ?? "Failed to load orders");
      } finally {
        setIsLoading(false);
      }
    },
    [apolloClient]
  );
  return {
    currentPage,
    orders: pageCache.get(currentPage) ?? [],
    total,
    totalPages: Math.ceil(total / PAGE_SIZE),
    isLoading,
    error,
    fetchPage,
    hasFetched: pageCache.size > 0
  };
}
const PackageIcon = ({ size, color }) => /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: "2", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M16.5 9.4L7.5 4.21M21 16V8C20.9996 7.6481 20.9071 7.3024 20.7315 6.99847C20.556 6.69454 20.3037 6.44327 20 6.27L13 2.27C12.696 2.09674 12.3511 2.0053 12 2.0053C11.6489 2.0053 11.304 2.09674 11 2.27L4 6.27C3.69626 6.44327 3.44398 6.69454 3.26846 6.99847C3.09294 7.3024 3.00036 7.6481 3 8V16C3.00036 16.3519 3.09294 16.6976 3.26846 17.0015C3.44398 17.3055 3.69626 17.5567 4 17.73L11 21.73C11.304 21.9033 11.6489 21.9947 12 21.9947C12.3511 21.9947 12.696 21.9033 13 21.73L20 17.73C20.3037 17.5567 20.556 17.3055 20.7315 17.0015C20.9071 16.6976 20.9996 16.3519 21 16Z",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx("path", { d: "M3.27002 6.96L12 12.01L20.73 6.96", strokeLinecap: "round", strokeLinejoin: "round" }),
  /* @__PURE__ */ jsx("path", { d: "M12 22.08V12", strokeLinecap: "round", strokeLinejoin: "round" })
] });
const ShoppingBagIcon = ({ size, color }) => /* @__PURE__ */ jsxs("svg", { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: "2", children: [
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6Z",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  ),
  /* @__PURE__ */ jsx("path", { d: "M3 6H21", strokeLinecap: "round", strokeLinejoin: "round" }),
  /* @__PURE__ */ jsx(
    "path",
    {
      d: "M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }
  )
] });
const cardStyles = `
  .oh-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }
  .oh-count {
    font-size: 13px;
    color: #6b7280;
  }
  .oh-cards {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 20px;
  }
  .oh-card {
    border: 1px solid #dcdddc;
    border-radius: 12px;
    padding: 14px;
    background: #fff;
  }
  .oh-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  .oh-order-id {
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }
  .oh-order-id a {
    color: #3b82f6;
    text-decoration: none;
  }
  .oh-order-id a:hover {
    text-decoration: underline;
  }
  .oh-order-date {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
  .oh-order-status {
    font-size: 11px;
    font-weight: 500;
    text-transform: capitalize;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
  }
  .oh-status-success {
    color: #059669;
    background: #ecfdf5;
  }
  .oh-status-other {
    color: #d97706;
    background: #fffbeb;
  }
  .oh-status-failed {
    color: #dc2626;
    background: #fef2f2;
  }
  .oh-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .oh-item-row {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .oh-item-image {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    overflow: hidden;
    background: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .oh-item-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .oh-item-details {
    flex: 1;
    min-width: 0;
  }
  .oh-item-title {
    font-size: 12px;
    color: #1a1a2e;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .oh-item-meta {
    font-size: 12px;
    color: #6b7280;
    display: flex;
    gap: 6px;
  }
  .oh-more-items {
    font-size: 12px;
    color: #6b7280;
  }
  .oh-card-footer {
    display: flex;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid #e5e7eb;
    font-size: 13px;
  }
  .oh-total-label {
    color: #6b7280;
  }
  .oh-total-amount {
    font-weight: 600;
    color: #1a1a2e;
  }
  .oh-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 14px;
    padding-top: 4px;
  }
  .oh-page-btn {
    border: none;
    background: none;
    font-size: 13px;
    font-weight: 500;
    color: #3b82f6;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
    transition: background-color 0.15s;
  }
  .oh-page-btn:hover:not(:disabled) {
    background: #eff6ff;
  }
  .oh-page-btn:disabled {
    color: #d1d5db;
    cursor: default;
  }
  .oh-page-info {
    font-size: 12px;
    color: #6b7280;
  }
  .oh-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 24px 0;
    color: #6b7280;
  }
  .oh-empty p {
    margin: 0;
    font-size: 14px;
    font-weight: 500;
  }
  .oh-empty span {
    font-size: 12px;
  }
  .oh-error {
    font-size: 13px;
    color: #dc2626;
    padding: 12px 0;
  }
  .oh-error a {
    color: #3b82f6;
    cursor: pointer;
    text-decoration: underline;
  }
  .oh-skeleton {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .oh-skeleton-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px;
    background: #fff;
  }
  .oh-skeleton-line {
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%);
    background-size: 200% 100%;
    animation: oh-shimmer 1.5s infinite;
  }
  @keyframes oh-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;
function OrderCardSkeleton() {
  return /* @__PURE__ */ jsx("div", { className: "oh-skeleton", children: [0, 1, 2].map((i) => /* @__PURE__ */ jsxs("div", { className: "oh-skeleton-card", children: [
    /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "50%", marginBottom: 8 } }),
    /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "30%", marginBottom: 14 } }),
    /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }, children: [
      /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: 40, height: 40, borderRadius: 6 } }),
      /* @__PURE__ */ jsxs("div", { style: { flex: 1 }, children: [
        /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "70%", marginBottom: 6 } }),
        /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "40%" } })
      ] })
    ] }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        style: { borderTop: "1px solid #e5e7eb", marginTop: 12, paddingTop: 10, display: "flex", justifyContent: "space-between" },
        children: [
          /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "20%" } }),
          /* @__PURE__ */ jsx("div", { className: "oh-skeleton-line", style: { width: "25%" } })
        ]
      }
    )
  ] }, i)) });
}
function OrderCardView({ order, orderDetailBasePath }) {
  const statusClass = getStatusClass(order.status);
  const orderUrl = `${orderDetailBasePath}/${order.orderId}`;
  return /* @__PURE__ */ jsxs("div", { className: "oh-card", children: [
    /* @__PURE__ */ jsxs("div", { className: "oh-card-header", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("div", { className: "oh-order-id", children: /* @__PURE__ */ jsxs("a", { href: orderUrl, target: "_blank", rel: "noopener noreferrer", children: [
          "Order #",
          order.orderId.slice(-8).toUpperCase()
        ] }) }),
        /* @__PURE__ */ jsx("div", { className: "oh-order-date", children: formatDate(order.orderDate) })
      ] }),
      /* @__PURE__ */ jsx("span", { className: `oh-order-status ${statusClass}`, children: formatStatus(order.status) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "oh-items", children: [
      order.items.slice(0, 2).map((item, idx) => /* @__PURE__ */ jsxs("div", { className: "oh-item-row", children: [
        /* @__PURE__ */ jsx("div", { className: "oh-item-image", children: item.imageUrl ? /* @__PURE__ */ jsx(
          FallbackImage,
          {
            src: item.imageUrl,
            fallbackImage: item.imageUrl.replace("media.brandclub.com", "media-api.brandclub.com"),
            alt: item.title
          }
        ) : /* @__PURE__ */ jsx(PackageIcon, { size: 20, color: "#9ca3af" }) }),
        /* @__PURE__ */ jsxs("div", { className: "oh-item-details", children: [
          /* @__PURE__ */ jsx("div", { className: "oh-item-title", children: item.title }),
          /* @__PURE__ */ jsxs("div", { className: "oh-item-meta", children: [
            /* @__PURE__ */ jsx("span", { children: formatMoney(item.price) }),
            /* @__PURE__ */ jsxs("span", { children: [
              "× ",
              item.quantity
            ] })
          ] })
        ] })
      ] }, idx)),
      order.items.length > 2 && /* @__PURE__ */ jsxs("div", { className: "oh-more-items", children: [
        "+",
        order.items.length - 2,
        " more item",
        order.items.length - 2 !== 1 ? "s" : ""
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "oh-card-footer", children: [
      /* @__PURE__ */ jsx("span", { className: "oh-total-label", children: "Total" }),
      /* @__PURE__ */ jsx("span", { className: "oh-total-amount", children: formatMoney(order.totalAmount) })
    ] })
  ] });
}
function OrderHistoryPart({ part }) {
  const { currentPage, orders, total, totalPages, isLoading, error, fetchPage, hasFetched } = useOrderHistoryPages();
  const { data: domainConfig } = useDomainConfig();
  const orderDetailBasePath = useMemo(
    () => `${window.location.origin}${getOrderDetailPath(domainConfig == null ? void 0 : domainConfig.storeBrandingType)}`,
    [domainConfig == null ? void 0 : domainConfig.storeBrandingType]
  );
  useEffect(() => {
    if (isOutputState(part.state)) {
      fetchPage(0);
    }
  }, [part.state]);
  if (isCallState(part.state)) return null;
  if (!isOutputState(part.state)) return null;
  return /* @__PURE__ */ jsxs("div", { className: "concierge-product-sections", children: [
    /* @__PURE__ */ jsx("style", { dangerouslySetInnerHTML: { __html: sectionFadeInStyle + cardStyles } }),
    /* @__PURE__ */ jsxs(
      "div",
      {
        className: "concierge-section",
        style: { animation: "concierge-fade-in 0.35s ease-out both" },
        children: [
          isLoading && !hasFetched && /* @__PURE__ */ jsx(OrderCardSkeleton, {}),
          error && !isLoading && /* @__PURE__ */ jsxs("div", { className: "oh-error", children: [
            "Failed to load orders.",
            " ",
            /* @__PURE__ */ jsx("a", { onClick: () => fetchPage(currentPage), children: "Try again" })
          ] }),
          !isLoading && !error && hasFetched && total === 0 && /* @__PURE__ */ jsxs("div", { className: "oh-empty", children: [
            /* @__PURE__ */ jsx(ShoppingBagIcon, { size: 40, color: "#9ca3af" }),
            /* @__PURE__ */ jsx("p", { children: "No orders found" }),
            /* @__PURE__ */ jsx("span", { children: "Your order history will appear here" })
          ] }),
          hasFetched && total > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs("div", { className: "oh-header", children: [
              /* @__PURE__ */ jsx("h3", { className: "concierge-section-title", children: "Your Orders" }),
              /* @__PURE__ */ jsxs("span", { className: "oh-count", children: [
                total,
                " order",
                total !== 1 ? "s" : ""
              ] })
            ] }),
            /* @__PURE__ */ jsx("div", { style: { opacity: isLoading ? 0.5 : 1, transition: "opacity 0.2s", pointerEvents: isLoading ? "none" : "auto" }, children: /* @__PURE__ */ jsx("div", { className: "oh-cards", children: orders.map((order) => /* @__PURE__ */ jsx(OrderCardView, { order, orderDetailBasePath }, order.orderId)) }) }),
            totalPages > 1 && /* @__PURE__ */ jsxs("div", { className: "oh-pagination", children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  className: "oh-page-btn",
                  disabled: currentPage === 0 || isLoading,
                  onClick: () => fetchPage(currentPage - 1),
                  children: "‹ Prev"
                }
              ),
              /* @__PURE__ */ jsxs("span", { className: "oh-page-info", children: [
                "Page ",
                currentPage + 1,
                " of ",
                totalPages
              ] }),
              /* @__PURE__ */ jsx(
                "button",
                {
                  className: "oh-page-btn",
                  disabled: currentPage >= totalPages - 1 || isLoading,
                  onClick: () => fetchPage(currentPage + 1),
                  children: "Next ›"
                }
              )
            ] })
          ] })
        ]
      }
    )
  ] });
}
export {
  OrderHistoryPart
};
//# sourceMappingURL=OrderHistoryPart.js.map
